import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

/**
 * Renders a list of available field names as clickable chips.
 * Root-level fields (no dots/brackets) use primary color styling.
 * Nested fields (contain . or []) use gray styling.
 *
 * Props:
 *   fields: string[] — list of field names to display
 *   onFieldClick: (fieldName: string) => void — callback when a chip is clicked
 *   label?: string — optional label shown above the chips
 */
export default function AvailableFieldsChips({ fields, onFieldClick, label }) {
  if (!fields || fields.length === 0) {
    return null;
  }

  return (
    <Container>
      {label && <ChipsLabel>{label}</ChipsLabel>}
      <ChipsWrapper>
        {fields.map((field) => {
          const isNested = field.includes('.') || field.includes('[');
          return (
            <Chip
              key={field}
              $nested={isNested}
              onClick={() => onFieldClick(field)}
              title={field}
              type="button"
            >
              {field}
            </Chip>
          );
        })}
      </ChipsWrapper>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ChipsLabel = styled.span`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textMuted};
`;

const ChipsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[1]};
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  cursor: pointer;
  border: 1px solid transparent;
  transition: ${theme.transitions.fast};
  line-height: 1.4;

  background: ${(p) => (p.$nested ? theme.colors.bgSecondary : theme.colors.primaryLight)};
  color: ${(p) => (p.$nested ? theme.colors.textSecondary : theme.colors.primary)};

  &:hover {
    border-color: ${(p) => (p.$nested ? theme.colors.borderHover : theme.colors.primaryBorder)};
    filter: brightness(0.95);
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;
